<template>
  <div class="w-full flex flex-col">
    <div class="mb-2.5">
      <ul class="text-sm mb-2.5 flex flex-col gap-1">
        <li>ρ = density (lb/ft<sup>3</sup>)</li>
        <li>V = volume flow rate (ft<sup>3</sup>/hour)</li>
        <li>Cp = specific heat (Btu/lb°F)</li>
        <li>T<sub>a</sub>-T<sub>b</sub> = temperature differential (°F)</li>
        <li>Q = ρ x V x Cp x (T<sub>a</sub>-T<sub>b</sub>)</li>
      </ul>
      <div class="font-bold text-center">Fill in four values to determine the fifth</div>
    </div>
    <div class="w-full flex flex-col gap-1">
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">ρ</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.density}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.density}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.density}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.density) }"
            v-model="state.density"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.density, state.density)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">V</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.volumeFlowRate}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.volumeFlowRate}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.volumeFlowRate}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.volumeFlowRate) }"
            v-model="state.volumeFlowRate"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.volumeFlowRate, state.volumeFlowRate)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">C<sub>p</sub></div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.specificHeat}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.specificHeat}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.specificHeat}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.specificHeat) }"
            v-model="state.specificHeat"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.specificHeat, state.specificHeat)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">T<sub>a</sub>-T<sub>b</sub></div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.temperatureDifferential}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.temperatureDifferential}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.temperatureDifferential}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.temperatureDifferential) }"
            v-model="state.temperatureDifferential"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.temperatureDifferential, state.temperatureDifferential)" />
        </label>
      </div>
      <div class="input-block w-full flex flex-col gap-0.5">
        <div class="w-full text-sm text-gray">Q</div>
        <label
          :for="`${calcType}-${CalculatorFieldsTypes.heatFlux}-input`"
          class="w-full">
          <input
            type="number"
            :id="`${calcType}-${CalculatorFieldsTypes.heatFlux}-input`"
            :title="`${calcType}-${CalculatorFieldsTypes.heatFlux}-input`"
            placeholder=""
            class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full"
            :class="{ 'border-blue-600 bg-yellow-100': state.inputFields.includes(CalculatorFieldsTypes.heatFlux) }"
            v-model="state.heatFlux"
            @input="updateInputAndOutputFields(CalculatorFieldsTypes.heatFlux, state.heatFlux)" />
        </label>
      </div>
      <div class="w-full flex items-center justify-center px-4 gap-2.5 mt-1">
        <div
          class="text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length === 0 }"
          @click="resetAction">
          Reset
        </div>
        <div
          class="w-full max-w-44 text-sm py-1.5 px-3 rounded text-white bg-red hover:bg-dark-red cursor-pointer flex items-center justify-center transition-colors duration-300"
          :class="{ 'grayscale pointer-events-none': state.inputFields.length < minInputFieldsQty }"
          @click="calculateAction">
          Calculate
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import CalculatorsHelper from '@/helpers/global/calculators.helper';
import GlobalHelper from '@/helpers/global/global.helper';

enum CalculatorFieldsTypes {
  density = 'density',
  volumeFlowRate = 'volume-flow-rate',
  specificHeat = 'specific-heat',
  temperatureDifferential = 'temperature-differential',
  heatFlux = 'heat-flux',
}

const state = reactive({
  density: '' as string | number,
  volumeFlowRate: '' as string | number,
  specificHeat: '' as string | number,
  temperatureDifferential: '' as string | number,
  heatFlux: '' as string | number,
  inputFields: [] as string[],
  outputFields: [] as string[],
});

const calculatorFieldsList: CalculatorFieldsTypes[] = [
  CalculatorFieldsTypes.density,
  CalculatorFieldsTypes.volumeFlowRate,
  CalculatorFieldsTypes.specificHeat,
  CalculatorFieldsTypes.temperatureDifferential,
  CalculatorFieldsTypes.heatFlux,
];
const calcType: string = 'heat-transfer-through-convection-calculator';
const minInputFieldsQty: number = 4;

const updateInputAndOutputFields = (field: CalculatorFieldsTypes, fieldValue: string | number) => {
  CalculatorsHelper.generateInputAndOutputFields(state.inputFields, state.outputFields, field, fieldValue, calcType, minInputFieldsQty);
  state.inputFields = CalculatorsHelper.getInputFields();
  state.outputFields = CalculatorsHelper.getOutputFields();
};

const setOutputFields = () => {
  calculatorFieldsList.forEach((field) => {
    state.outputFields.push(field);
  });
};

const resetAction = () => {
  state.density = '';
  state.volumeFlowRate = '';
  state.specificHeat = '';
  state.temperatureDifferential = '';
  state.heatFlux = '';
  state.inputFields = [];
  state.outputFields = [];

  setOutputFields();
};

const calculateAction = () => {
  if (state.inputFields.length === minInputFieldsQty) {
    const densityValue = Number(state.density);
    const volumeFlowRateValue = Number(state.volumeFlowRate);
    const specificHeatValue = Number(state.specificHeat);
    const temperatureDifferentialValue = Number(state.temperatureDifferential);
    const heatFluxValue = Number(state.heatFlux);

    const firstOutputField = state.outputFields[0];

    switch (firstOutputField) {
      case CalculatorFieldsTypes.density:
        state.density = GlobalHelper.roundToDecimals(heatFluxValue / (volumeFlowRateValue * specificHeatValue * temperatureDifferentialValue), 4);
        break;
      case CalculatorFieldsTypes.volumeFlowRate:
        state.volumeFlowRate = GlobalHelper.roundToDecimals(heatFluxValue / (densityValue * specificHeatValue * temperatureDifferentialValue), 4);
        break;
      case CalculatorFieldsTypes.specificHeat:
        state.specificHeat = GlobalHelper.roundToDecimals(heatFluxValue / (densityValue * volumeFlowRateValue * temperatureDifferentialValue), 4);
        break;
      case CalculatorFieldsTypes.temperatureDifferential:
        state.temperatureDifferential = GlobalHelper.roundToDecimals(heatFluxValue / (densityValue * volumeFlowRateValue * specificHeatValue), 4);
        break;
      case CalculatorFieldsTypes.heatFlux:
        state.heatFlux = GlobalHelper.roundToDecimals(densityValue * volumeFlowRateValue * specificHeatValue * temperatureDifferentialValue, 4);
        break;
    }
  }
};

onMounted(() => {
  setOutputFields();
});
</script>
